import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Volume2, Pause, Play, Square, Loader2, Film } from 'lucide-react';
import { VideoQuizPlayer } from './VideoQuizPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

export const AccessibilityBar = ({ module, authHeader = {}, onVideoQuizCompleted }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!module?.id) return;
    setStatus(null);
    stopAudio();
    axios.get(`${API}/api/accessibility/status/${module.id}`, authHeader)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
    return () => stopAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module?.id]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
    setPaused(false);
  };

  const startAudio = async () => {
    if (audioRef.current) {
      document.querySelector("[data-testid='lernvideo-video']")?.pause();
      audioRef.current.play();
      setPaused(false);
      setPlaying(true);
      return;
    }
    setLoading(true);
    try {
      document.querySelector("[data-testid='lernvideo-video']")?.pause();
      const res = await axios.post(`${API}/api/accessibility/tts/${module.id}`, {}, {
        ...authHeader, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audio.onended = () => stopAudio();
      audioRef.current = audio;
      await audio.play();
      setPlaying(true);
      setPaused(false);
    } catch (e) {
      stopAudio();
    }
    setLoading(false);
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPaused(true);
      setPlaying(false);
    }
  };

  if (!status?.tts_available && !status?.video_available) return null;

  return (
    <div className="mb-6 rounded-2xl border border-cream-300 bg-cream-100 p-4" data-testid="accessibility-bar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-espresso font-semibold">
          <Volume2 className="w-5 h-5 text-terra" />
          <span>Barrierefrei lernen</span>
        </div>
        {status.tts_available && (loading ? (
          <button disabled className="flex items-center gap-2 px-4 py-2 rounded-full bg-terra/60 text-white text-sm font-semibold" data-testid="tts-loading-btn">
            <Loader2 className="w-4 h-4 animate-spin" /> Audio wird vorbereitet …
          </button>
        ) : playing ? (
          <button onClick={pauseAudio} className="flex items-center gap-2 px-4 py-2 rounded-full bg-terra text-white text-sm font-semibold hover:bg-terra-dark transition-colors" data-testid="tts-pause-btn">
            <Pause className="w-4 h-4" /> Pause
          </button>
        ) : (
          <button onClick={startAudio} className="flex items-center gap-2 px-4 py-2 rounded-full bg-terra text-white text-sm font-semibold hover:bg-terra-dark transition-colors" data-testid="tts-play-btn">
            <Play className="w-4 h-4" /> {paused ? 'Weiter vorlesen' : 'Modul vorlesen'}
          </button>
        ))}
        {(playing || paused) && (
          <button onClick={stopAudio} className="flex items-center gap-2 px-3 py-2 rounded-full border border-cream-300 text-espresso text-sm hover:bg-cream-200 transition-colors" data-testid="tts-stop-btn">
            <Square className="w-3.5 h-3.5" /> Stopp
          </button>
        )}
        {status.video_available && (
          <button onClick={() => setShowVideo((v) => !v)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-terra text-terra text-sm font-semibold hover:bg-terra hover:text-white transition-colors" data-testid="lernvideo-toggle-btn">
            <Film className="w-4 h-4" /> {showVideo ? 'Lernvideo ausblenden' : 'Lernvideo ansehen'}
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-espresso/60">
        {status.tts_available
          ? 'Der komplette Lerninhalt dieses Moduls wird mit natürlicher Stimme vorgelesen.'
          : 'Lernvideo mit integrierten Verständnisfragen zu diesem Modul.'}
      </p>
      {showVideo && status.video_available && (
        <div className="mt-4 rounded-xl overflow-hidden border border-cream-300 bg-black" data-testid="lernvideo-player">
          <VideoQuizPlayer src={status.video_url} moduleId={module.id} authHeader={authHeader} onCompleted={onVideoQuizCompleted} onInterrupt={pauseAudio} />
        </div>
      )}
      {showVideo && status.video_available && status.video_updated && (
        <p className="mt-1.5 text-[11px] text-sand-500 font-mono" data-testid="lernvideo-version">
          Videoversion vom {status.video_updated}
        </p>
      )}
    </div>
  );
};

export default AccessibilityBar;
